"use client";

import type { ReactNode } from "react";
import {
  BadgeCheck,
  Camera,
  Captions,
  Check,
  FileText,
  Heart,
  ListChecks,
  MapPin,
  Megaphone,
  MessageCircle,
  Navigation,
  PhoneCall,
  Play,
  Scissors,
  Search,
  Share2,
  Sparkles,
} from "lucide-react";
import { BrowserFrame, PhoneFrame } from "./frames";
import { SAMPLE_BUSINESS, SAMPLE_HOST, Stars } from "./showcase-mockups";

/* ─────────────────────────────────────────────────────────────
 * Mockups der Dienstleistungsseiten.
 *
 * Gleiche Regeln wie auf der Landingpage: echte Oberflächen statt
 * grauer Balken, ein erkennbarer Beispielbetrieb, KEINE erfundenen
 * Ergebnis-Zahlen. Wo eine Seite mit einer belegten Statistik
 * arbeitet (Klickanteile je Google-Position auf der SEO-Seite), nutzt
 * das Mockup genau diese Werte – sonst nichts.
 * ──────────────────────────────────────────────────────────── */

/** Äußere Bühne: Karte mit weichem Farbverlauf. */
export function Stage({
  children,
  tint = "from-brand-400/15 via-transparent to-emerald-400/10",
  className = "",
  fill = true,
}: {
  children: ReactNode;
  tint?: string;
  className?: string;
  /** Rasterzelle ausfüllen (Standard). Aus bei fester Mindesthöhe, damit Texte darunter auf einer Linie starten. */
  fill?: boolean;
}) {
  return (
    <div
      className={`relative flex flex-col justify-center rounded-2xl border p-4 sm:p-5 ${fill ? "h-full" : ""} ${className}`}
      style={{ borderColor: "var(--border)", backgroundColor: "var(--bg-secondary)" }}
    >
      <div className={`pointer-events-none absolute inset-0 rounded-2xl bg-gradient-to-br ${tint}`} />
      <div className="relative w-full">{children}</div>
    </div>
  );
}

export function Pill({ children, tone = "gray" }: { children: ReactNode; tone?: "gray" | "brand" | "success" | "warning" }) {
  const map = {
    gray: { bg: "var(--fi-gray-bg)", fg: "var(--fi-gray-fg)" },
    brand: { bg: "var(--fi-brand-bg)", fg: "var(--fi-brand-fg)" },
    success: { bg: "var(--fi-success-bg)", fg: "var(--fi-success-fg)" },
    warning: { bg: "var(--fi-warning-bg)", fg: "var(--fi-warning-fg)" },
  } as const;
  return (
    <span className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[9px] font-semibold" style={{ backgroundColor: map[tone].bg, color: map[tone].fg }}>
      {children}
    </span>
  );
}

export const cardStyle = { borderColor: "var(--border)", backgroundColor: "var(--card-bg)" } as const;

/* ═════════════════════════════════════════════════════════════
 * WEBDESIGN
 * ═════════════════════════════════════════════════════════════ */

const SERP_ROWS = [
  { rank: 1, name: "Ihr Unternehmen", domain: "ihr-unternehmen.de", ctr: 28.5, own: true },
  { rank: 2, name: "Mitbewerber A", domain: "mitbewerber-a.de", ctr: 15.7 },
  { rank: 3, name: "Mitbewerber B", domain: "mitbewerber-b.de", ctr: 11.0 },
  { rank: 4, name: "Branchen-Portal", domain: "branchen-portal.de", ctr: 8.0 },
  { rank: 5, name: "Verzeichnis", domain: "verzeichnis.de", ctr: 7.2 },
];

export function SerpCtrMockup() {
  return (
    <Stage tint="from-blue-400/10 via-transparent to-brand-400/15">
      <BrowserFrame host="google.de">
        <div className="px-3 py-3">
          <div className="flex items-center gap-2 rounded-full border px-3 py-1.5 text-[10px]" style={{ borderColor: "var(--border)", backgroundColor: "var(--bg)", color: "var(--text-secondary)" }}>
            <Search className="h-3 w-3" />
            <span className="truncate">Ihr wichtigster Suchbegriff</span>
          </div>
          <p className="mt-2.5 text-[9px] font-semibold uppercase tracking-wider" style={{ color: "var(--text-quaternary)" }}>
            Anteil der Klicks je Platz
          </p>
          <div className="mt-1.5 space-y-1.5">
            {SERP_ROWS.map((r) => (
              <div
                key={r.rank}
                className="rounded-lg border p-2"
                style={r.own ? { borderColor: "var(--fi-brand-fg)", backgroundColor: "var(--fi-brand-bg)" } : cardStyle}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-[8px]" style={{ color: "var(--text-tertiary)" }}>
                      {r.domain}
                    </p>
                    <p className="truncate text-[11px] font-medium" style={{ color: "#1A0DAB" }}>
                      {r.name}
                    </p>
                  </div>
                  <span
                    className="shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-bold"
                    style={r.own ? { backgroundColor: "var(--fi-brand-fg)", color: "white" } : { backgroundColor: "var(--bg-secondary)", color: "var(--text-tertiary)" }}
                  >
                    Platz {r.rank}
                  </span>
                </div>
                <div className="mt-1.5 flex items-center gap-2">
                  <div className="h-1.5 flex-1 rounded-full" style={{ backgroundColor: "var(--border)" }}>
                    <div
                      className="h-1.5 rounded-full"
                      style={{ width: `${(r.ctr / 28.5) * 100}%`, backgroundColor: r.own ? "var(--fi-brand-fg)" : "var(--text-quaternary)" }}
                    />
                  </div>
                  <span className="w-10 text-right text-[9px] font-semibold tabular-nums" style={{ color: r.own ? "var(--fi-brand-fg)" : "var(--text-tertiary)" }}>
                    {r.ctr.toFixed(1).replace(".", ",")} %
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </BrowserFrame>
    </Stage>
  );
}

export function LocalMapsMockup() {
  return (
    <Stage tint="from-emerald-400/10 via-transparent to-blue-400/10">
      <div className="overflow-hidden rounded-xl border" style={{ ...cardStyle, boxShadow: "var(--card-shadow-lg)" }}>
        <div className="relative h-24" style={{ background: "linear-gradient(135deg, #C7E9C0 0%, #A8DADC 55%, #D8E2DC 100%)" }}>
          <svg viewBox="0 0 300 96" className="absolute inset-0 h-full w-full" aria-hidden="true">
            <path d="M0,60 C60,40 120,80 180,50 S260,30 300,44" fill="none" stroke="white" strokeWidth="5" strokeOpacity="0.8" />
            <path d="M90,0 C100,30 80,60 110,96" fill="none" stroke="white" strokeWidth="3" strokeOpacity="0.7" />
          </svg>
          <span className="absolute left-[44%] top-[28%] flex h-7 w-7 items-center justify-center rounded-full text-white" style={{ backgroundColor: "var(--color-brand-700)", boxShadow: "0 0 0 6px rgba(21,128,61,0.2)" }}>
            <MapPin className="h-4 w-4" />
          </span>
          <span className="absolute left-[18%] top-[55%] h-2.5 w-2.5 rounded-full bg-white/90 ring-2 ring-gray-400" />
          <span className="absolute left-[74%] top-[38%] h-2.5 w-2.5 rounded-full bg-white/90 ring-2 ring-gray-400" />
        </div>
        <div className="space-y-1 p-2.5">
          {[
            { name: "Ihr Unternehmen", meta: "Geöffnet · in Ihrer Nähe", stars: 5, own: true },
            { name: "Mitbewerber A", meta: "Schließt bald", stars: 4 },
            { name: "Mitbewerber B", meta: "Geöffnet", stars: 4 },
          ].map((r) => (
            <div
              key={r.name}
              className="flex items-center justify-between gap-2 rounded-md px-2 py-1.5"
              style={r.own ? { backgroundColor: "var(--fi-brand-bg)", border: "1px solid var(--fi-brand-fg)" } : { border: "1px solid var(--border)" }}
            >
              <div className="min-w-0">
                <p className="truncate text-[10px] font-semibold" style={{ color: r.own ? "var(--fi-brand-fg)" : "var(--text)" }}>
                  {r.name}
                </p>
                <p className="text-[8px]" style={{ color: "var(--text-tertiary)" }}>
                  {r.meta}
                </p>
              </div>
              <Stars n={r.stars} />
            </div>
          ))}
          <div className="flex gap-1 pt-0.5">
            {[
              { Icon: PhoneCall, t: "Anrufen" },
              { Icon: Navigation, t: "Route" },
            ].map(({ Icon, t }) => (
              <span key={t} className="inline-flex flex-1 items-center justify-center gap-1 rounded-full border py-1 text-[9px] font-semibold" style={{ borderColor: "var(--border)", color: "var(--text-secondary)" }}>
                <Icon className="h-2.5 w-2.5 text-brand-500" />
                {t}
              </span>
            ))}
          </div>
        </div>
      </div>
    </Stage>
  );
}

/** Aufbau eines Monatsberichts – zeigt, WAS berichtet wird, ohne erfundene Werte. */
function ReportMockup({
  title,
  done,
  metrics,
  barColor,
}: {
  title: string;
  done: string[];
  metrics: string[];
  barColor: string;
}) {
  const shapes = [
    [30, 42, 38, 55, 60, 72],
    [48, 40, 52, 50, 64, 70],
    [26, 34, 44, 40, 58, 66],
  ];
  return (
    <div className="overflow-hidden rounded-xl border" style={{ ...cardStyle, boxShadow: "var(--card-shadow-lg)" }}>
      <div className="flex items-center justify-between border-b px-3 py-2" style={{ borderColor: "var(--border)", backgroundColor: "var(--bg-secondary)" }}>
        <p className="inline-flex items-center gap-1.5 text-[10px] font-semibold" style={{ color: "var(--text)" }}>
          <FileText className="h-3 w-3 text-brand-500" />
          {title}
        </p>
        <Pill tone="gray">Beispiel-Aufbau</Pill>
      </div>
      <div className="p-3">
        <p className="inline-flex items-center gap-1 text-[9px] font-semibold uppercase tracking-wider" style={{ color: "var(--text-quaternary)" }}>
          <ListChecks className="h-2.5 w-2.5" /> Was wurde gemacht
        </p>
        <ul className="mt-1 space-y-1">
          {done.map((d) => (
            <li key={d} className="flex items-center gap-1.5 text-[10px]" style={{ color: "var(--text-secondary)" }}>
              <Check className="h-2.5 w-2.5 shrink-0 text-brand-500" />
              {d}
            </li>
          ))}
        </ul>
        <p className="mt-2.5 text-[9px] font-semibold uppercase tracking-wider" style={{ color: "var(--text-quaternary)" }}>
          Wie hat es sich entwickelt
        </p>
        <div className="mt-1 grid grid-cols-3 gap-1.5">
          {metrics.map((m, i) => (
            <div key={m} className="rounded-md border p-1.5" style={{ borderColor: "var(--border)", backgroundColor: "var(--bg-secondary)" }}>
              <p className="truncate text-[8px] font-medium" style={{ color: "var(--text-tertiary)" }}>
                {m}
              </p>
              <div className="mt-1.5 flex h-10 items-end gap-0.5">
                {shapes[i % shapes.length].map((h, j) => (
                  <span key={j} className="flex-1 rounded-t" style={{ height: `${h}%`, backgroundColor: j === 5 ? barColor : "var(--border-secondary)" }} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const CAMPAIGNS = [
  { name: "Suche · Leistung + Ort", platform: "Google", status: "Aktiv", tone: "success" as const, pace: 72 },
  { name: "Anzeigentext A gegen B", platform: "Google", status: "Test läuft", tone: "brand" as const, pace: 48 },
  { name: "Zielgruppe Region", platform: "Meta", status: "Lernphase", tone: "warning" as const, pace: 30 },
];
export function AdsCampaignMockup() {
  return (
    <Stage tint="from-blue-400/10 via-transparent to-pink-400/10">
      <div className="grid gap-3 sm:grid-cols-5">
        {/* Anzeigen-Vorschau */}
        <div className="min-w-0 space-y-2 sm:col-span-2">
          <div className="rounded-xl border p-2.5" style={{ ...cardStyle, boxShadow: "var(--card-shadow-lg)" }}>
            <div className="flex items-center gap-1.5">
              <span className="rounded-sm px-1 text-[8px] font-bold text-white" style={{ backgroundColor: "#1A73E8" }}>
                Anzeige
              </span>
              <span className="truncate text-[9px]" style={{ color: "var(--text-tertiary)" }}>
                {SAMPLE_HOST}
              </span>
            </div>
            <p className="mt-1 text-[11px] font-medium leading-snug" style={{ color: "#1A0DAB" }}>
              Heizung & Bad vom Meisterbetrieb
            </p>
            <p className="mt-0.5 text-[9px] leading-snug" style={{ color: "var(--text-tertiary)" }}>
              Schriftliches Angebot, verlässliche Termine. Jetzt anfragen.
            </p>
          </div>
          <div className="overflow-hidden rounded-xl border" style={{ ...cardStyle, boxShadow: "var(--card-shadow-lg)" }}>
            <div className="flex items-center gap-1.5 px-2.5 py-1.5">
              <span className="h-4 w-4 rounded-full" style={{ background: "linear-gradient(135deg, #4267B2, #E1306C)" }} />
              <div className="min-w-0">
                <p className="truncate text-[9px] font-semibold" style={{ color: "var(--text)" }}>
                  {SAMPLE_BUSINESS}
                </p>
                <p className="text-[7px]" style={{ color: "var(--text-tertiary)" }}>
                  Gesponsert
                </p>
              </div>
            </div>
            <div className="flex h-14 items-center justify-center" style={{ background: "linear-gradient(135deg, var(--color-brand-400), #0E7490)" }}>
              <Megaphone className="h-5 w-5 text-white/85" />
            </div>
            <div className="flex items-center justify-between gap-2 px-2.5 py-1.5">
              <p className="truncate text-[9px] font-medium" style={{ color: "var(--text-secondary)" }}>
                Neues Bad in Ihrer Region
              </p>
              <span className="shrink-0 rounded border px-1.5 py-0.5 text-[8px] font-semibold" style={{ borderColor: "var(--border-secondary)", color: "var(--text-secondary)" }}>
                Mehr dazu
              </span>
            </div>
          </div>
        </div>

        {/* Kampagnen-Übersicht */}
        <div className="min-w-0 overflow-hidden rounded-xl border sm:col-span-3" style={{ ...cardStyle, boxShadow: "var(--card-shadow-lg)" }}>
          <div className="flex items-center justify-between border-b px-3 py-2" style={{ borderColor: "var(--border)", backgroundColor: "var(--bg-secondary)" }}>
            <p className="text-[10px] font-semibold" style={{ color: "var(--text)" }}>
              Kampagnen
            </p>
            <Pill tone="success">
              <BadgeCheck className="h-2.5 w-2.5" /> Anfragen-Messung aktiv
            </Pill>
          </div>
          <div className="space-y-2 p-3">
            {CAMPAIGNS.map((c) => (
              <div key={c.name}>
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="truncate text-[10px] font-semibold" style={{ color: "var(--text)" }}>
                      {c.name}
                    </p>
                    <p className="text-[8px]" style={{ color: "var(--text-tertiary)" }}>
                      {c.platform} · Monatsbudget
                    </p>
                  </div>
                  <Pill tone={c.tone}>{c.status}</Pill>
                </div>
                <div className="mt-1 h-1.5 rounded-full" style={{ backgroundColor: "var(--border)" }}>
                  <div className="h-1.5 rounded-full" style={{ width: `${c.pace}%`, backgroundColor: "var(--color-brand-500)" }} />
                </div>
              </div>
            ))}
            <div className="border-t pt-2" style={{ borderColor: "var(--border)" }}>
              <p className="text-[9px] font-semibold uppercase tracking-wider" style={{ color: "var(--text-quaternary)" }}>
                Gemessen wird
              </p>
              <div className="mt-1 flex flex-wrap gap-1">
                {["Anfrage-Formular", "Anruf-Klick", "Terminbuchung"].map((m) => (
                  <Pill key={m} tone="brand">
                    <Check className="h-2.5 w-2.5" />
                    {m}
                  </Pill>
                ))}
              </div>
            </div>
            <p className="text-[8px]" style={{ color: "var(--text-quaternary)" }}>
              Balken = verbrauchtes Monatsbudget. Beispiel-Oberfläche.
            </p>
          </div>
        </div>
      </div>
    </Stage>
  );
}

/* ═════════════════════════════════════════════════════════════
 * SOCIAL MEDIA
 * ═════════════════════════════════════════════════════════════ */

export function ReelPhoneMockup() {
  return (
    <Stage tint="from-pink-400/10 via-transparent to-violet-400/10">
      <div className="flex items-center justify-center gap-4">
        <PhoneFrame width={150} className="relative shrink-0">
          <div className="relative h-full w-full" style={{ background: "linear-gradient(160deg, #0F766E 0%, #1D4ED8 55%, #7C3AED 100%)" }}>
            <div className="absolute left-2 top-5 rounded bg-white/90 px-1.5 py-0.5 text-[8px] font-bold text-black">Reel</div>
            <div className="absolute inset-x-3 top-[24%] rounded-md bg-black/35 px-2 py-1 text-center text-[9px] font-bold leading-tight text-white backdrop-blur">
              So läuft eine Heizungswartung bei uns ab
            </div>
            <div className="absolute left-1/2 top-[44%] flex h-9 w-9 -translate-x-1/2 items-center justify-center rounded-full bg-white/25 backdrop-blur">
              <Play className="ml-0.5 h-4 w-4 fill-white text-white" />
            </div>
            <div className="absolute right-1.5 top-[52%] space-y-2.5 text-white">
              <Heart className="h-3.5 w-3.5" />
              <MessageCircle className="h-3.5 w-3.5" />
              <Share2 className="h-3.5 w-3.5" />
            </div>
            <div className="absolute inset-x-2 bottom-3 text-white">
              <p className="text-[8px] font-semibold">@muster.haustechnik</p>
              <p className="mt-0.5 text-[8px] leading-snug text-white/90">Einblick aus der Werkstatt – echte Arbeit, echtes Team.</p>
              <div className="mt-1.5 h-0.5 w-full rounded-full bg-white/30">
                <div className="h-0.5 w-2/5 rounded-full bg-white" />
              </div>
            </div>
          </div>
        </PhoneFrame>
        <ul className="hidden space-y-2 sm:block">
          {[
            { Icon: Camera, t: "Vor Ort gedreht" },
            { Icon: Sparkles, t: "Starker Einstieg" },
            { Icon: Captions, t: "Untertitel" },
            { Icon: Scissors, t: "Schnitt je Plattform" },
          ].map(({ Icon, t }) => (
            <li key={t} className="flex items-center gap-2 rounded-lg border px-2.5 py-1.5 text-[11px] font-medium" style={{ ...cardStyle, color: "var(--text-secondary)" }}>
              <Icon className="h-3.5 w-3.5 text-brand-500" />
              {t}
            </li>
          ))}
        </ul>
      </div>
    </Stage>
  );
}

export function SocialReportMockup() {
  return (
    <Stage tint="from-pink-400/10 via-transparent to-brand-400/10">
      <ReportMockup
        title="Social-Media-Monatsbericht"
        done={["Drehtag vor Ort", "Beiträge nach Plan veröffentlicht", "Kommentare beantwortet"]}
        metrics={["Reichweite", "Follower", "Interaktionen"]}
        barColor="#ec4899"
      />
    </Stage>
  );
}

/* ═════════════════════════════════════════════════════════════
 * BRANDING
 * ═════════════════════════════════════════════════════════════ */

